import React, { useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { useHistory } from 'react-router-dom';
import tw from 'twin.macro';
import styled from 'styled-components';
import cogoToast from 'cogo-toast';
import app from 'services/firebase';
import { fetchPartners, fetchFoods, addFood, updateFood, deleteFood } from 'DBServices/firestoreDBService';
import Navbar from 'components/Navbar';
import AnimationRevealPage from 'helpers/AnimationRevealPage.js';
import AdminProductForm from 'components/admin/AdminProductForm';

const auth = getAuth(app);

const Container = tw.div`relative -mx-8 mt-0 bg-gray-100 min-h-screen`;
const Content = tw.div`max-w-4xl mx-auto py-10 px-4`;
const Card = tw.div`bg-white rounded-xl shadow p-6 mb-6`;
const Title = tw.h2`text-xl font-bold text-gray-800 mb-1`;
const Sub = tw.p`text-sm text-gray-500 mb-4`;
const Label = tw.span`text-xs font-bold text-gray-500 uppercase tracking-wider`;
const Value = tw.p`text-sm text-gray-800 mt-0 mb-3`;
const Grid = tw.div`grid grid-cols-1 sm:grid-cols-2 gap-4`;
const Pill = tw.span`inline-block px-3 py-1 rounded-full text-xs font-bold mr-1 mb-1`;

const StatusBadge = styled.div`
    ${tw`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold`}
    ${({ status }) =>
        status === 'approved' ? tw`bg-green-100 text-green-700` :
        status === 'rejected' ? tw`bg-red-100 text-red-700` :
        tw`bg-yellow-100 text-yellow-700`}
`;

const PARTNER_TYPE_LABELS = {
    market: '🏪 Market',
    farm: '🌾 Farm',
    wholesale: '📦 Wholesale Store',
    dispatcher: '🚚 Dispatcher/Driver',
    logistics: '🏭 Logistics Partner',
};

export default function PartnerDashboard() {
    const history = useHistory();
    const [user, setUser] = useState(null);
    const [partner, setPartner] = useState(null);
    const [loading, setLoading] = useState(true);

    const [foods, setFoods] = useState([]);
    const [editingFood, setEditingFood] = useState(null);
    const [showFoodForm, setShowFoodForm] = useState(false);

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (u) => {
            if (!u) { history.replace('/login'); return; }
            setUser(u);
            try {
                const all = await fetchPartners();
                const mine = all.find(p => p.uid === u.uid);
                setPartner(mine || null);
                if (mine && mine.partnerType === 'market' && mine.verificationStatus === 'approved') {
                    const allFoods = await fetchFoods();
                    setFoods(allFoods.filter(f => f.partnerMarketId === mine.firestoreId));
                }
            } catch (e) {
                cogoToast.error('Failed to load partner profile.');
            } finally {
                setLoading(false);
            }
        });
        return unsub;
    }, [history]);

    if (loading) return <AnimationRevealPage><Navbar /><Container><Content><p style={{ color: '#6b7280', marginTop: '3rem', textAlign: 'center' }}>Loading your dashboard...</p></Content></Container></AnimationRevealPage>;

    if (!partner) return (
        <AnimationRevealPage>
            <Navbar />
            <Container>
            <Content>
                <Card>
                    <Title>Partner Profile Not Found</Title>
                    <Sub>We couldn't find a partner application linked to your account.</Sub>
                    <p style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                        If you just signed up, please wait a moment and refresh. If the issue persists, contact support.
                    </p>
                    <button
                        onClick={() => signOut(auth).then(() => history.replace('/login'))}
                        style={{ marginTop: '1rem', padding: '0.5rem 1.25rem', background: '#ef4444', color: 'white', borderRadius: 8, fontWeight: 700, fontSize: '0.85rem', border: 'none', cursor: 'pointer' }}
                    >
                        Sign Out
                    </button>
                </Card>
            </Content>
            </Container>
        </AnimationRevealPage>
    );

    return (
        <AnimationRevealPage>
            <Navbar />
            <Container>
            <Content>
                {/* Status banner */}
                <Card>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
                        <div>
                            <Title>{partner.businessName}</Title>
                            <Sub>{PARTNER_TYPE_LABELS[partner.partnerType] || partner.partnerType} · {partner.name}</Sub>
                            <StatusBadge status={partner.verificationStatus}>
                                {partner.verificationStatus === 'approved' && '✅ Approved'}
                                {partner.verificationStatus === 'pending' && '⏳ Pending Review'}
                                {partner.verificationStatus === 'rejected' && '❌ Application Rejected'}
                            </StatusBadge>
                            {partner.verificationStatus === 'pending' && (
                                <p style={{ fontSize: '0.8rem', color: '#92400e', marginTop: '0.75rem', background: '#fef3c7', padding: '0.5rem 0.75rem', borderRadius: 8 }}>
                                    Your application is under review. We'll notify you once it's approved. This usually takes 1–2 business days.
                                </p>
                            )}
                            {partner.verificationStatus === 'rejected' && (
                                <p style={{ fontSize: '0.8rem', color: '#991b1b', marginTop: '0.75rem', background: '#fee2e2', padding: '0.5rem 0.75rem', borderRadius: 8 }}>
                                    Your application was not approved. Please contact support for more information.
                                </p>
                            )}
                        </div>
                        <button
                            onClick={() => signOut(auth).then(() => history.replace('/login'))}
                            style={{ padding: '0.5rem 1.25rem', background: '#ef4444', color: 'white', borderRadius: 8, fontWeight: 700, fontSize: '0.85rem', border: 'none', cursor: 'pointer' }}
                        >
                            Sign Out
                        </button>
                    </div>
                </Card>

                {/* Business details */}
                <Card>
                    <Title>Business Information</Title>
                    <Grid>
                        <div>
                            <Label>Business Name</Label>
                            <Value>{partner.businessName}</Value>
                            <Label>Business Address</Label>
                            <Value>{partner.businessAddress || '—'}</Value>
                            <Label>Business Phone</Label>
                            <Value>{partner.businessPhone || partner.phone || '—'}</Value>
                            <Label>Business Email</Label>
                            <Value>{partner.businessEmail || partner.email || '—'}</Value>
                        </div>
                        <div>
                            <Label>Partner Type</Label>
                            <Value>{PARTNER_TYPE_LABELS[partner.partnerType] || partner.partnerType}</Value>
                            {partner.vehicleType && <><Label>Vehicle Type</Label><Value>{partner.vehicleType}</Value></>}
                            {partner.warehouseCapacity && <><Label>Warehouse Capacity</Label><Value>{partner.warehouseCapacity}</Value></>}
                            {partner.certifications && <><Label>Certifications</Label><Value>{partner.certifications}</Value></>}
                            <Label>Submitted</Label>
                            <Value>{partner.submittedAt ? new Date(partner.submittedAt).toLocaleDateString() : '—'}</Value>
                        </div>
                    </Grid>

                    {partner.description && (
                        <>
                            <Label>Description</Label>
                            <Value>{partner.description}</Value>
                        </>
                    )}

                    {partner.serviceAreas?.length > 0 && (
                        <>
                            <Label>Service Areas</Label>
                            <div style={{ marginTop: '0.25rem' }}>
                                {partner.serviceAreas.map(a => (
                                    <Pill key={a} style={{ background: '#d1fae5', color: '#065f46' }}>{a}</Pill>
                                ))}
                            </div>
                        </>
                    )}
                </Card>

                {/* Assigned orders placeholder */}
                {partner.verificationStatus === 'approved' && (
                    <Card>
                        <Title>Assigned Orders</Title>
                        <Sub>Orders assigned to you will appear here.</Sub>
                        <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af', fontStyle: 'italic', background: '#f9fafb', borderRadius: 8 }}>
                            No orders assigned yet.
                        </div>
                    </Card>
                )}

                {/* Market Foods Section */}
                {partner.partnerType === 'market' && partner.verificationStatus === 'approved' && (
                    <Card>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <Title style={{ marginBottom: 0 }}>My Market Foods</Title>
                            <button onClick={() => { setEditingFood(null); setShowFoodForm(true); }} style={{ padding: '0.5rem 1rem', background: '#10b981', color: 'white', borderRadius: 8, fontWeight: 600, fontSize: '0.85rem' }}>+ Add Food</button>
                        </div>
                        {foods.length === 0 ? (
                            <>
                                <p style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: '1rem' }}>You haven't added any foods yet.</p>
                                <div style={{ padding: '1rem', background: '#fffbeb', color: '#b45309', borderRadius: 8, fontSize: '0.85rem', border: '1px solid #fde68a' }}>
                                    <strong>Note:</strong> Your market will not be visible to customers in the marketplace until you add at least one food item.
                                </div>
                            </>
                        ) : (
                            <div style={{ overflowX: 'auto', marginTop: '1rem' }}>
                                <table style={{ width: '100%', textAlign: 'left', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                            <th style={{ padding: '0.5rem', color: '#6b7280', fontSize: '0.75rem', textTransform: 'uppercase' }}>Image</th>
                                            <th style={{ padding: '0.5rem', color: '#6b7280', fontSize: '0.75rem', textTransform: 'uppercase' }}>Name</th>
                                            <th style={{ padding: '0.5rem', color: '#6b7280', fontSize: '0.75rem', textTransform: 'uppercase' }}>Price</th>
                                            <th style={{ padding: '0.5rem', color: '#6b7280', fontSize: '0.75rem', textTransform: 'uppercase' }}>Status</th>
                                            <th style={{ padding: '0.5rem', color: '#6b7280', fontSize: '0.75rem', textTransform: 'uppercase' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {foods.map(f => (
                                            <tr key={f.firestoreId} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                                <td style={{ padding: '0.5rem' }}>
                                                    {f.imageSrc ? <img src={f.imageSrc} alt={f.title} style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} /> : '-'}
                                                </td>
                                                <td style={{ padding: '0.5rem', fontWeight: 600, fontSize: '0.9rem' }}>{f.title}</td>
                                                <td style={{ padding: '0.5rem', fontSize: '0.9rem' }}>{f.price ? `₦${f.price}` : '-'}</td>
                                                <td style={{ padding: '0.5rem' }}>
                                                    <StatusBadge status={f.status || 'pending'}>
                                                        {f.status === 'approved' && '✅ Live'}
                                                        {f.status === 'rejected' && '❌ Rejected'}
                                                        {(!f.status || f.status === 'pending') && '⏳ Awaiting Approval'}
                                                    </StatusBadge>
                                                </td>
                                                <td style={{ padding: '0.5rem' }}>
                                                    <button onClick={() => { setEditingFood(f); setShowFoodForm(true); }} style={{ color: '#3b82f6', fontSize: '0.8rem', marginRight: '0.5rem', fontWeight: 600 }}>Edit</button>
                                                    <button onClick={async () => {
                                                        if (window.confirm(`Delete ${f.title}?`)) {
                                                            await deleteFood(f.firestoreId);
                                                            setFoods(foods.filter(x => x.firestoreId !== f.firestoreId));
                                                        }
                                                    }} style={{ color: '#ef4444', fontSize: '0.8rem', fontWeight: 600 }}>Delete</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Card>
                )}

                {showFoodForm && (
                    <AdminProductForm
                        collectionLabel="Foods"
                        item={editingFood}
                        isPartner={true}
                        onCancel={() => { setShowFoodForm(false); setEditingFood(null); }}
                        onSave={async (data) => {
                            data.partnerMarketId = partner.firestoreId; // Force it to their market
                            data.status = 'pending'; // Require admin approval
                            if (editingFood?.firestoreId) {
                                await updateFood(editingFood.firestoreId, data);
                            } else {
                                await addFood(data);
                            }
                            const allFoods = await fetchFoods();
                            setFoods(allFoods.filter(f => f.partnerMarketId === partner.firestoreId));
                            setShowFoodForm(false);
                            setEditingFood(null);
                        }}
                    />
                )}
            </Content>
            </Container>
        </AnimationRevealPage>
    );
}
